import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import ExcelJS from "exceljs";
import ProductStoreMatrixApi from "../api/ProductStoreMatrixApi";
import StoreGroupApi from "../api/StoreGroupApi";
// import { ContactSupportOutlined, PermPhoneMsg } from "@mui/icons-material";

const ViewProductStoreMatrix = () => {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeGroups, setStoreGroups] = useState({});
  const [eCommerceStore, setECommerceStore] = useState(null); // Single ECOMMERCE store
  const [mappings, setMappings] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [womensProducts, setWomensProducts] = useState([]);
  const [mensProducts, setMensProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("mens"); // Default to "mens"
  const [columnOffsets, setColumnOffsets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const columnRefs = useRef([]);

  useLayoutEffect(() => {
    const calculateColumnOffsets = () => {
      const offsets = [];
      let cumulativeOffset = 0;

      columnRefs.current.forEach((ref, index) => {
        if (ref) {
          const width = ref.offsetWidth;
          offsets[index] = cumulativeOffset;
          cumulativeOffset += width;
        }
      });

      setColumnOffsets(offsets);
    };

    calculateColumnOffsets();
    window.addEventListener("resize", calculateColumnOffsets);
    return () => window.removeEventListener("resize", calculateColumnOffsets);
  }, [products]);

  useEffect(() => {
    const fetchMatrixData = async () => {
      try {
        setIsLoading(true);

        const productData = await ProductStoreMatrixApi.fetchAll();
        const { products, stores, mappings } = productData.data;

        const groupingData = await StoreGroupApi.fetchStoreGroupings();

        setProducts(products);

        const womens = products.filter((product) =>
          product.DIVISION.toUpperCase().includes("WOMENS")
        );
        const mens = products.filter(
          (product) => !product.DIVISION.toUpperCase().includes("WOMENS")
        );

        setWomensProducts(womens);
        setMensProducts(mens);
        setStores(stores);

        // Process mappings for efficient access
        const formattedMappings = mappings.reduce((acc, mapping) => {
          acc[`${mapping.style_color}-${mapping.store_id}`] =
            mapping.is_selected;
          return acc;
        }, {});

        // Find the ECOMMERCE store
        const eComStore = stores.find(
          (store) => store.STORE_NAME === "ECOMMERCE"
        );
        setECommerceStore(eComStore);

        // Process groupings for mens and womens
        const groupMap = {
          mens: {},
          womens: {},
        };

        groupingData.data.forEach((grouping) => {
          const { store_id, groups } = grouping;

          ["mens", "womens"].forEach((division) => {
            if (groups[division]) {
              const groupKey = groups[division];

              if (!groupMap[division][groupKey]) {
                groupMap[division][groupKey] = [];
              }

              const store = stores.find(
                (store) => parseInt(store.id) === store_id
              );

              if (store) {
                groupMap[division][groupKey].push(store);
              }
            }
          });
        });

        setMappings(formattedMappings);
        setStoreGroups(groupMap);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatrixData();
  }, []);

  const filteredProducts =
    activeTab === "womens"
      ? womensProducts.filter((product) =>
          `${product.STYLE_CODE} ${product.STYLE_DESC_ENG} ${product.COLOR_CODE} ${product.COLOR_ENG} ${product.DEPARTMENT} ${product.DIVISION}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      : mensProducts.filter((product) =>
          `${product.STYLE_CODE} ${product.STYLE_DESC_ENG} ${product.COLOR_CODE} ${product.COLOR_ENG} ${product.DEPARTMENT} ${product.DIVISION}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
  useLayoutEffect(() => {
    const calculateColumnOffsets = () => {
      const offsets = [];
      let cumulativeOffset = 0;

      columnRefs.current.forEach((ref, index) => {
        if (ref) {
          const width = ref.offsetWidth;
          offsets[index] = cumulativeOffset;
          cumulativeOffset += width;
        }
      });

      // Only update state if offsets have changed
      setColumnOffsets((prevOffsets) => {
        const isSame =
          prevOffsets.length === offsets.length &&
          prevOffsets.every((value, index) => value === offsets[index]);
        if (!isSame) {
          return offsets;
        }
        return prevOffsets; // No state update if offsets are unchanged
      });
    };

    calculateColumnOffsets();

    // Add event listener to update offsets on window resize
    window.addEventListener("resize", calculateColumnOffsets);

    // Cleanup on component unmount
    return () => window.removeEventListener("resize", calculateColumnOffsets);
  }, [filteredProducts]); // Trigger recalculation on `filteredProducts` change

  const handleDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const groupColors = [
      "F8CBAD", // Group A
      "C6E0B4", // Group B
      "F4B084", // Group C
      "FFC7CE", // Group D
      "E2EFDA", // Group E
      "FFC000", // Group F
    ];
    const ecommerceColor = "FF0000";
    const productHeaderColor = "BDD7EE";

    const addSheet = (sheetName, products, activeTab) => {
      const worksheet = workbook.addWorksheet(sheetName);

      const productHeaders = [
        "Style",
        "Description",
        "CC",
        "Color",
        "Department",
        "Division",
        "MSRP",
        "Floorset Code",
        "ECOMMERCE",
      ];

      const groupHeadersRow = worksheet.getRow(1);
      const productHeadersRow = worksheet.getRow(2);
      const storeColumnMap = {}; // Map for tracking store column indices

      // Add headers for product details
      productHeaders.forEach((header, index) => {
        const cell1 = groupHeadersRow.getCell(index + 1);
        const cell2 = productHeadersRow.getCell(index + 1);
        cell2.value = header;

        [cell1, cell2].forEach((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb:
                header === "ECOMMERCE" ? ecommerceColor : productHeaderColor,
            },
          };
          cell.font = { name: "Arial", bold: true };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // Apply background color for the entire column for product details
        worksheet
          .getColumn(index + 1)
          .eachCell({ includeEmpty: true }, (cell, rowNumber) => {
            if (rowNumber > 2) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: productHeaderColor },
              };
              cell.alignment = { horizontal: "left", vertical: "middle" };
            }
          });
      });

      let colIndex = productHeaders.length + 1;
      const activeGroup = storeGroups[activeTab] || {};

      // Sort groups alphabetically but leave stores unsorted
      const sortedGroups = Object.entries(activeGroup).sort(
        ([groupA], [groupB]) => groupA.localeCompare(groupB)
      );

      // Add group headers and store names
      sortedGroups.forEach(([group, stores], groupIndex) => {
        const startCol = colIndex;
        const endCol = colIndex + stores.length - 1;

        // Merge cells for group headers
        worksheet.mergeCells(1, startCol, 1, endCol);
        const cell = groupHeadersRow.getCell(startCol);
        cell.value = `Group ${group}`;
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: groupColors[groupIndex % groupColors.length] },
        };
        cell.font = { name: "Arial", bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        stores.forEach((store, storeIndex) => {
          const storeCol = colIndex + storeIndex;
          const storeCell = productHeadersRow.getCell(storeCol);
          storeCell.value = store.STORE_NAME;
          storeCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: groupColors[groupIndex % groupColors.length] },
          };
          storeCell.font = { name: "Arial", bold: true };
          storeCell.alignment = { horizontal: "center" };
          storeCell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          storeColumnMap[`${store.id}`] = storeCol; // Map store ID to its column index
        });

        colIndex += stores.length;
      });

      // Add the "Total" column header at the end
      const totalHeaderCol = colIndex;
      const totalHeaderCell = productHeadersRow.getCell(totalHeaderCol);
      const aboveTotalHeaderCell = groupHeadersRow.getCell(totalHeaderCol);
      aboveTotalHeaderCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: productHeaderColor },
      };
      totalHeaderCell.value = "Total";
      totalHeaderCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: productHeaderColor },
      };
      totalHeaderCell.font = { name: "Arial", bold: true };
      totalHeaderCell.alignment = { horizontal: "center", vertical: "middle" };
      totalHeaderCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Add product rows
      const totals = Array(totalHeaderCol).fill(0); // Initialize totals for each column
      products.forEach((product, rowIndex) => {
        let productTotal = 0;
        const productRow = [
          product.STYLE_CODE,
          product.STYLE_DESC_ENG,
          product.COLOR_CODE,
          product.COLOR_ENG,
          product.DEPARTMENT,
          product.DIVISION,
          product.STYLE_MSRP,
          product.FLOOR_SET_CODE,
          eCommerceStore
            ? mappings[`${product.STYLE_COLOR}-${eCommerceStore.id}`]
              ? "✓"
              : ""
            : "",
        ];

        // Add store data
        sortedGroups.forEach(([_, stores]) => {
          stores.forEach((store) => {
            const key = `${product.STYLE_COLOR}-${store.id}`;
            const value = mappings[key] ? "✓" : "";
            productRow.push(value);

            if (value === "✓") {
              productTotal += 1; // Increment product total
              totals[storeColumnMap[store.id] - 1] += 1; // Increment store total
            }
          });
        });

        // Add eCommerce totals if applicable
        if (eCommerceStore) {
          const eComKey = `${product.STYLE_COLOR}-${eCommerceStore.id}`;
          if (mappings[eComKey]) {
            totals[productHeaders.length - 1] += 1;
            productTotal += 1;
          }
        }

        productRow.push(productTotal); // Add product total as the last column
        const row = worksheet.addRow(productRow);

        // Left-align specific columns, center-align others
        row.eachCell((cell, cellIndex) => {
          if (cellIndex >= 1 && cellIndex <= 6) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          } else {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          }
        });
      });

      // Add totals row
      const totalsRow = worksheet.addRow(new Array(colIndex).fill(""));

      // Merge the first 6 cells for "Totals"
      worksheet.mergeCells(totalsRow.number, 1, totalsRow.number, 6);
      const mergedCell = totalsRow.getCell(1);
      mergedCell.value = "Totals:";
      mergedCell.font = { name: "Arial", bold: true };
      mergedCell.alignment = { horizontal: "right", vertical: "middle" };

      // Calculate the total sum for the last column
      let totalSum = 0;

      // Set the totals for the remaining cells
      totals.forEach((total, index) => {
        const cell = totalsRow.getCell(index + 1);
        if (index >= 8) {
          const value = total || 0;
          totalSum += value; // Add to the total sum
          cell.value = value; // Default to 0 if total is empty
          cell.font = { name: "Arial" };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Set the total sum in the last cell
      const lastCell = totalsRow.getCell(colIndex);
      lastCell.value = totalSum; // Set the sum of all previous totals
      lastCell.font = { name: "Arial" };
      lastCell.alignment = { horizontal: "center", vertical: "middle" };
      lastCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // Adjust column widths dynamically
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const value = cell.value ? cell.value.toString() : "";
          maxLength = Math.max(maxLength, value.length);
        });
        column.width = maxLength + 3; // Add padding
      });

      // Apply colors after all data is set
      worksheet.columns.forEach((column, colIndex) => {
        // Product details columns (Style to Division)
        if (colIndex >= 0 && colIndex < 8) {
          column.eachCell({ includeEmpty: true }, (cell, rowIndex) => {
            if (rowIndex > 2) {
              // Skip headers
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: productHeaderColor },
              };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            }
          });
        }
      });

      // Apply group colors to columns after all data is written
      let currentGroupColIndex = productHeaders.length + 1; // Start after product headers

      sortedGroups.forEach(([group, stores], groupIndex) => {
        const groupColor = groupColors[groupIndex % groupColors.length]; // Get group color

        // Iterate through the store columns for this group
        stores.forEach(() => {
          worksheet
            .getColumn(currentGroupColIndex)
            .eachCell({ includeEmpty: true }, (cell, rowIndex) => {
              if (rowIndex > 2) {
                // Skip headers
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: groupColor },
                };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
              }
            });

          currentGroupColIndex++; // Move to the next column
        });
      });

      // Freeze the first six columns
      worksheet.views = [{ state: "frozen", xSplit: 6 }];
    };

    // Add sheets for mens and womens
    addSheet("Mens Division", mensProducts, "mens");
    addSheet("Womens Division", womensProducts, "womens");

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "StoreMatrix.xlsx";
    link.click();
  };

  const calculateStoreTotals = () => {
    // Initialize totals for each store
    const totals = {};

    // Add totals for non-eCommerce stores grouped by activeTab groups
    Object.entries(storeGroups[activeTab] || {})
      .sort(([groupA], [groupB]) => groupA.localeCompare(groupB)) // Sort groups alphabetically
      .flatMap(([group, stores]) => stores)
      .forEach((store) => {
        totals[store.id] = filteredProducts.reduce((sum, product) => {
          const key = `${product.STYLE_COLOR}-${store.id}`;
          return sum + (mappings[key] ? 1 : 0);
        }, 0);
      });

    // Add total for eCommerce store if it exists
    if (eCommerceStore) {
      totals[eCommerceStore.id] = filteredProducts.reduce((sum, product) => {
        const key = `${product.STYLE_COLOR}-${eCommerceStore.id}`;
        return sum + (mappings[key] ? 1 : 0);
      }, 0);
    }

    return totals;
  };

  const storeTotals = calculateStoreTotals(
    [
      eCommerceStore,
      ...Object.values(storeGroups[activeTab] || {}).flat(),
    ].filter(Boolean)
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "75vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 1 }}>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        View Store Matrix
      </Typography>
      <Divider sx={{ marginY: 2 }} />

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        indicatorColor="primary"
        textColor="primary"
        sx={{ marginBottom: 2 }}
      >
        <Tab label={`Men's Division (${mensProducts.length})`} value="mens" />
        <Tab
          label={`Women's Division (${womensProducts.length})`}
          value="womens"
        />
      </Tabs>

      <Box
        sx={{
          display: "flex",
          justifyContent: "right",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="contained" color="success" onClick={handleDownload}>
            Download
          </Button>
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        style={{
          overflowX: "auto",
          overflowY: "auto",
          height: "calc(100vh - 300px)", // Dynamically adjust height based on screen size
          maxHeight: "calc(100vh - 335px)", // Max height based on available viewport
        }}
      >
        <Table stickyHeader size="small" sx={{ border: "0.5px solid #ddd" }}>
          <TableHead>
            <TableRow
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 3,
                background: "#e9edf7",
              }}
            >
              {[
                "Style",
                "Description",
                "CC",
                "Color",
                "Department",
                "Gender",
              ].map((header, index) => (
                <TableCell
                  key={header}
                  rowSpan={2}
                  ref={(el) => (columnRefs.current[index + 1] = el)}
                  style={{
                    position: "sticky",
                    top: 0,
                    left: columnOffsets[index + 1] || 0,
                    zIndex: 3,
                    fontWeight: "bold",
                    background: "#dfe6f3",
                    whiteSpace: "nowrap",
                    fontSize: "11px",
                    fontFamily: "'Roboto', sans-serif",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {header}
                </TableCell>
              ))}
              <TableCell
                rowSpan={2}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  background: "#dfe6f3",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                MSRP
              </TableCell>
              <TableCell
                rowSpan={2}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  background: "#dfe6f3",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                Floorset Code
              </TableCell>
              {eCommerceStore && (
                <TableCell
                  rowSpan={2}
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    background: "#f6f7f7",
                    fontSize: "11px",
                    fontFamily: "'Roboto', sans-serif",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {eCommerceStore.STORE_NAME}
                </TableCell>
              )}
              {Object.entries(storeGroups[activeTab] || {})
                .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                .map(([group, stores]) => (
                  <TableCell
                    key={group}
                    colSpan={stores.length}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      fontWeight: "bold",
                      background: "#e9edf7",
                      fontSize: "11px",
                      fontFamily: "'Roboto', sans-serif",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      border: "1px solid #ddd",
                    }}
                  >
                    Group {group}
                  </TableCell>
                ))}
              {/* Totals Column */}
              <TableCell
                rowSpan={2}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  background: "#f6f7f7",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                  textAlign: "center",
                }}
              >
                Total
              </TableCell>
            </TableRow>
            {/* Grouped Store Headers */}
            <TableRow
              sx={{
                position: "sticky",
                top: 38,
                zIndex: 2,
                background: "#f6f7f7",
              }}
            >
              {Object.entries(storeGroups[activeTab] || {})
                .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                .flatMap(([group, stores]) =>
                  stores.map((store) => (
                    <TableCell
                      key={store.id}
                      style={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        background: "#f6f7f7",
                        fontSize: "11px",
                        fontFamily: "'Roboto', sans-serif",
                        position: "sticky",
                        top: 38,
                        zIndex: 2,
                        border: "0.5px solid #ddd",
                      }}
                    >
                      {store.STORE_NAME}
                    </TableCell>
                  ))
                )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product, index) => {
              const totalSelected = [
                eCommerceStore,
                ...Object.values(storeGroups[activeTab] || {}).flat(),
              ]
                .filter(Boolean)
                .reduce((sum, store) => {
                  const key = `${product.STYLE_COLOR}-${store.id}`;
                  return sum + (mappings[key] ? 1 : 0);
                }, 0);

              return (
                <TableRow
                  key={product.STYLE_COLOR}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f9f9f8" : "#ffffff",
                  }}
                >
                  {[
                    product.STYLE_CODE,
                    product.STYLE_DESC_ENG,
                    product.COLOR_CODE,
                    product.COLOR_ENG,
                    product.DEPARTMENT,
                    product.DIVISION,
                  ].map((content, colIndex) => (
                    <TableCell
                      key={colIndex}
                      style={{
                        position: "sticky",
                        left: columnOffsets[colIndex + 1] || 0,
                        background: "#dfe6f3",
                        whiteSpace: "nowrap",
                        fontSize: "11px",
                        fontFamily: "'Roboto', sans-serif",
                        border: "0.5px solid #ddd",
                      }}
                    >
                      {content}
                    </TableCell>
                  ))}
                  <TableCell
                    style={{
                      background: "#dfe6f3",
                      fontSize: "11px",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    <b>$</b>
                    {product.STYLE_MSRP}
                  </TableCell>
                  <TableCell
                    style={{
                      background: "#dfe6f3",
                      fontSize: "11px",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    {product.FLOOR_SET_CODE}
                  </TableCell>
                  {eCommerceStore && (
                    <TableCell
                      align="center"
                      style={{
                        whiteSpace: "nowrap",
                        fontSize: "11px",
                        fontFamily: "'Roboto', sans-serif",
                        border: "0.5px solid #ddd",
                      }}
                    >
                      {mappings[`${product.STYLE_COLOR}-${eCommerceStore.id}`]
                        ? "✓"
                        : ""}
                    </TableCell>
                  )}
                  {Object.entries(storeGroups[activeTab] || {})
                    .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                    .flatMap(([group, stores]) =>
                      stores.map((store) => (
                        <TableCell
                          key={store.id}
                          align="center"
                          style={{
                            whiteSpace: "nowrap",
                            fontSize: "11px",
                            fontFamily: "'Roboto', sans-serif",
                            border: "0.5px solid #ddd",
                          }}
                        >
                          {mappings[`${product.STYLE_COLOR}-${store.id}`]
                            ? "✓"
                            : ""}
                        </TableCell>
                      ))
                    )}
                  {/* Total Column */}
                  <TableCell
                    align="center"
                    style={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      fontSize: "11px",
                      fontFamily: "'Roboto', sans-serif",
                      border: "0.5px solid #ddd",
                    }}
                  >
                    {totalSelected}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          {/* Totals Row */}
          <tfoot>
            <TableRow sx={{ background: "#f6f7f7", height: 20 }} rowHeight={20}>
              <TableCell
                colSpan={6}
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 3,
                  fontWeight: "bold",
                  textAlign: "right",
                  background: "#f6f7f7",
                  left: 0,
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  fontFamily: "'Roboto', sans-serif",
                  border: "0.5px solid #ddd",
                }}
              >
                Totals:
              </TableCell>
              <TableCell
                colSpan={2}
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 1,
                  fontWeight: "bold",
                  fontSize: "11px",
                  textAlign: "right",
                  background: "#f6f7f7",
                }}
              ></TableCell>
              {eCommerceStore && (
                <TableCell
                  style={{
                    position: "sticky",
                    bottom: 0,
                    fontWeight: "bold",
                    background: "#f6f7f7",
                    textAlign: "center",
                    zIndex: 1,
                    fontSize: "11px",
                    border: "0.5px solid #ddd",
                  }}
                >
                  {storeTotals[eCommerceStore.id] || 0}
                </TableCell>
              )}
              {Object.entries(storeGroups[activeTab] || {})
                .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
                .flatMap(([group, stores]) =>
                  stores.map((store) => (
                    <TableCell
                      key={store.id}
                      style={{
                        position: "sticky",
                        bottom: 0,
                        fontWeight: "bold",
                        background: "#f6f7f7",
                        textAlign: "center",
                        fontSize: "11px",
                        zIndex: 1,
                        border: "0.5px solid #ddd",
                      }}
                    >
                      {storeTotals[store.id] || 0}
                    </TableCell>
                  ))
                )}
              <TableCell
                style={{
                  position: "sticky",
                  bottom: 0,
                  fontWeight: "bold",
                  background: "#f6f7f7",
                  textAlign: "center",
                  zIndex: 1,
                  fontSize: "11px",
                  border: "0.5px solid #ddd",
                }}
              >
                {Object.values(storeTotals).reduce(
                  (sum, total) => sum + total,
                  0
                )}
              </TableCell>
            </TableRow>
          </tfoot>
        </Table>
      </TableContainer>

      <Typography
        variant="body2"
        align="left"
        sx={{
          marginTop: 2,
          fontWeight: "bold",
          fontSize: "11px",
          fontFamily: "'Roboto', sans-serif",
        }}
      >
        Showing 1 to {filteredProducts.length} for {activeTab}
      </Typography>
    </Box>
  );
};

export default ViewProductStoreMatrix;
